import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Badge, Button, Card } from '../components/ui'
import { routeFor } from '../funnel/useFunnel'
import { ErrorPanel, PageSkeleton } from '../components/Layout'
import { eventOnce, funnelApi, initialSessionId } from '../lib/api'

/**
 * The AHA screen. Everything on it has to be traceably theirs — the chips are
 * their own answers played back, the first question is chosen from their branch
 * and depth, and the "why this works" line answers the barrier they named.
 *
 * The transparency line is not decoration: it's the guardrail that keeps this a
 * recommendation rather than a personality or compatibility read.
 */
export function ResultScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [depthNudge, setDepthNudge] = useState(0)

  const result = useQuery({
    queryKey: ['funnel-result', initialSessionId],
    queryFn: () => funnelApi.result(initialSessionId),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (!result.data) return
    void eventOnce(initialSessionId, 'ritual_revealed').catch(() => {})
    void eventOnce(initialSessionId, 'result_viewed').catch(() => {})
  }, [result.data])

  if (result.isPending) return <PageSkeleton />
  if (result.isError || !result.data) return <ErrorPanel retry={() => void result.refetch()} />

  const data = result.data
  const { reflect } = data
  const depth = Math.min(4, Math.max(1, reflect.depth + depthNudge))
  const depthKey = (['light', 'playful', 'deep', 'bold'] as const)[depth - 1]
  const barrierLine = reflect.barrier
    ? t(`result.barrierAnswer.${reflect.barrier}`, { minutes: reflect.minutes, defaultValue: '' })
    : ''

  const adjustDepth = (delta: number) => {
    setDepthNudge((current) => {
      const next = Math.min(4, Math.max(1, reflect.depth + current + delta)) - reflect.depth
      void funnelApi
        .event(initialSessionId, 'depth_adjusted', {
          depth: reflect.depth + next,
          action: delta < 0 ? 'lighter' : 'deeper',
        })
        .catch(() => {})
      return next
    })
  }

  return (
    <div className="content-column">
      <p className="eyebrow">{t('result.eyebrow')}</p>
      <h1 className="value-title">{data.title}</h1>

      {/* Reflect-back: their own answers, in their own terms. */}
      <section className="reflect-block" aria-label={t('result.reflectEyebrow')}>
        <p className="eyebrow">{t('result.reflectEyebrow')}</p>
        <div className="chip-row">
          <Badge rotate={-2}>{t(`result.contexts.${reflect.context}`)}</Badge>
          <Badge color="var(--sky)" rotate={2}>
            {t(`questions.connection_goal.options.${reflect.goal}`)}
          </Badge>
          <Badge color="var(--cloud)" rotate={-1}>
            {reflect.minutes} {t('result.minutes')}
          </Badge>
          <Badge color="var(--meadow)" rotate={2}>
            {t(`result.paces.${data.pace}`)}
          </Badge>
          {reflect.boundaryCount > 0 ? (
            <Badge color="var(--lilac)" rotate={-2}>
              {t('result.boundaries', { count: reflect.boundaryCount })}
            </Badge>
          ) : null}
        </div>
        <p className="fine-print">{t('result.reflectBody')}</p>
      </section>

      {/* The actual first question, rendered the way the app renders it. */}
      <Card accent="var(--sunshine)">
        <p className="eyebrow">{t('result.sample')}</p>
        <p className="sample-prompt">{data.samplePrompt}</p>
      </Card>

      <section className="mix-block">
        <p className="eyebrow">{t('result.mix')}</p>
        <div className="mix-bars">
          {(['light', 'playful', 'meaningful'] as const).map((key, index) => (
            <div className="chart-row" key={key}>
              <span className="chart-key">{t(`insight.labels.${key === 'meaningful' ? 'deep' : key === 'light' ? 'warm' : 'playful'}`)}</span>
              <span className="chart-track">
                <span
                  className="chart-fill"
                  style={{
                    width: `${(data.questionMix[key] / 8) * 100}%`,
                    animationDelay: `${index * 120}ms`,
                  }}
                />
              </span>
            </div>
          ))}
        </div>
        <div className="depth-controls">
          <span className="pace-label">
            {t('result.yourPace')}: <strong>{t(`result.depths.${depthKey}`)}</strong>
          </span>
          <div className="depth-buttons">
            <Button size="sm" variant="cloud" onClick={() => adjustDepth(-1)} disabled={depth <= 1}>
              {t('result.lighter')}
            </Button>
            <Button size="sm" variant="cloud" onClick={() => adjustDepth(1)} disabled={depth >= 4}>
              {t('result.deeper')}
            </Button>
          </div>
        </div>
      </section>

      <section className="why-block">
        <p className="eyebrow">{t('result.whyTitle')}</p>
        {barrierLine ? <p className="body-copy barrier-line">{barrierLine}</p> : null}
        <p className="body-copy">{t('result.whyBody')}</p>
      </section>

      <Button block size="lg" variant="sunshine" onClick={() => navigate(routeFor('confirm'))}>
        {t('result.cta')}
      </Button>
      <p className="fine-print">{t('result.transparent')}</p>
    </div>
  )
}
